import { useCallback, useEffect, useState } from 'react'
import {
  fetchBlockedIps,
  fetchEmailLog,
  fetchStats,
  setIpBlocked,
  sendEmailBatch,
  type BlockedIpRow,
  type EmailLogResponse,
  type SendBatchResponse,
  type StatsResponse,
} from './api'
import { BarChart, LineChart, type Series } from './Chart'
import { SessionList } from './SessionList'
import { SessionDetail } from './SessionDetail'
import './admin.css'

const TOKEN_KEY = 'tdee_admin_token'

// Local dev bypasses the password gate (the Worker also skips auth on
// localhost — see admin.ts). Any non-empty token lets the UI proceed.
const DEV_BYPASS_TOKEN = 'dev-local'

// Hash routes:
//   ''                       → dashboard (cards + trend + countries + demographics)
//   '#/emails'               → Email outbox (send-batch + log)
//   '#/sessions'             → Recent sessions list
//   '#/sessions/<uuid>'      → single session detail
type AdminView = 'dashboard' | 'emails' | 'sessions' | 'blocked'
type Route =
  | { view: 'dashboard' }
  | { view: 'emails' }
  | { view: 'sessions' }
  | { view: 'blocked' }
  | { view: 'session'; id: string }

function readRoute(): Route {
  const h = typeof window !== 'undefined' ? window.location.hash : ''
  const detail = h.match(/^#\/sessions\/([A-Za-z0-9_-]+)/)
  if (detail) return { view: 'session', id: detail[1] }
  if (h === '#/emails') return { view: 'emails' }
  if (h === '#/sessions') return { view: 'sessions' }
  if (h === '#/blocked') return { view: 'blocked' }
  return { view: 'dashboard' }
}

const COLORS = {
  sessions: '#2563eb',
  messages: '#16a34a',
  calcs: '#d97706',
}

// Acquisition channels, in stack order (bottom → top) for the daily bars and
// left → right in the legend. Paid first so a spend change is read against a
// fixed baseline instead of a segment that moves around, then the earned
// channels, with direct/other last — they are the residual, not a channel we
// operate. Keys mirror the server's SourceChannel union (routes/handoff.ts).
const SOURCE_ORDER = [
  'google_ads',
  'openai',
  'x',
  'reddit',
  'google_organic',
  'other',
  'direct',
] as const

const SOURCE_META: Record<string, { label: string; color: string }> = {
  google_ads: { label: 'Google Ads', color: '#2563eb' },
  openai: { label: 'ChatGPT', color: '#10a37f' },
  x: { label: 'X', color: '#0f172a' },
  reddit: { label: 'Reddit', color: '#ff4500' },
  google_organic: { label: 'Google organic', color: '#7c3aed' },
  other: { label: 'Other referral', color: '#d97706' },
  direct: { label: 'Direct', color: '#94a3b8' },
}

function readToken(): string {
  try {
    return sessionStorage.getItem(TOKEN_KEY) ?? ''
  } catch {
    return ''
  }
}
function writeToken(t: string) {
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t)
    else sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

export default function AdminApp() {
  const [token, setToken] = useState<string>(() => {
    if (import.meta.env.DEV) return DEV_BYPASS_TOKEN
    return readToken()
  })

  // Belt-and-braces: in dev mode, force the token even if a stale empty value
  // survived HMR / Fast Refresh. The Worker also skips auth on localhost.
  useEffect(() => {
    if (import.meta.env.DEV && !token) {
      setToken(DEV_BYPASS_TOKEN)
    }
  }, [token])
  const [pwInput, setPwInput] = useState<string>('')
  const [stats, setStats] = useState<StatsResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [exclude, setExclude] = useState<boolean>(true)
  const [tab, setTab] = useState<'daily' | 'weekly'>('daily')
  const [route, setRoute] = useState(() => readRoute())

  useEffect(() => {
    const onHash = () => setRoute(readRoute())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const openSession = useCallback((id: string) => {
    window.location.hash = `#/sessions/${id}`
  }, [])
  const backToDashboard = useCallback(() => {
    window.location.hash = ''
  }, [])

  const load = useCallback(
    async (t: string, ex: boolean) => {
      setLoading(true)
      setError(null)
      try {
        const data = await fetchStats(t, ex)
        setStats(data)
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'failed'
        if (msg === 'unauthorized') {
          writeToken('')
          setToken('')
          setError('Wrong password.')
        } else {
          setError(msg)
        }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    if (token) void load(token, exclude)
  }, [token, exclude, load])

  const onLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (!pwInput) return
    writeToken(pwInput)
    setToken(pwInput)
    setPwInput('')
  }

  const onLogout = () => {
    writeToken('')
    setToken('')
    setStats(null)
  }

  if (!token) {
    return (
      <div className="admin-shell">
        <form className="admin-login" onSubmit={onLogin}>
          <h1>TDEE Dashboard</h1>
          <p className="muted">Enter the admin password to continue.</p>
          <input
            type="password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            placeholder="Password"
            autoFocus
          />
          <button type="submit">Sign in</button>
          {error && <div className="admin-error">{error}</div>}
        </form>
      </div>
    )
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div>
          <h1>TDEE Dashboard</h1>
          {stats && (
            <p className="muted">
              Updated {new Date(stats.generated_at).toLocaleString()} · UTC date buckets
            </p>
          )}
        </div>
        <div className="admin-controls">
          <label className="admin-toggle">
            <input
              type="checkbox"
              checked={exclude}
              onChange={(e) => setExclude(e.target.checked)}
            />
            Exclude dogfood ({stats?.excluded_ids.length ?? 3} ids)
          </label>
          <button className="ghost" onClick={() => token && load(token, exclude)} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button className="ghost" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </header>

      {error && <div className="admin-error">{error}</div>}

      {route.view !== 'session' && <AdminNav current={route.view} />}

      {route.view === 'session' && (
        <SessionDetail token={token} id={route.id} onBack={backToDashboard} />
      )}
      {route.view === 'dashboard' && stats && (
        <DashboardBody stats={stats} tab={tab} setTab={setTab} />
      )}
      {route.view === 'emails' && stats && <EmailOutboxPanel token={token} />}
      {route.view === 'sessions' && stats && (
        <SessionList token={token} exclude={exclude} onOpen={openSession} />
      )}
      {route.view === 'blocked' && <BlockedIpsPanel token={token} />}
    </div>
  )
}

function AdminNav({ current }: { current: AdminView }) {
  const tabs: Array<{ key: AdminView; label: string; href: string }> = [
    { key: 'dashboard', label: 'Overview', href: '#/' },
    { key: 'emails', label: 'Email outbox', href: '#/emails' },
    { key: 'sessions', label: 'Sessions', href: '#/sessions' },
    { key: 'blocked', label: 'Blocked IPs', href: '#/blocked' },
  ]
  return (
    <nav className="admin-tabs">
      {tabs.map((t) => (
        <a
          key={t.key}
          href={t.href}
          className={t.key === current ? 'admin-tab on' : 'admin-tab'}
        >
          {t.label}
        </a>
      ))}
    </nav>
  )
}

function DashboardBody({
  stats,
  tab,
  setTab,
}: {
  stats: StatsResponse
  tab: 'daily' | 'weekly'
  setTab: (t: 'daily' | 'weekly') => void
}) {
  const t = stats.totals
  const cards: Array<[string, string | number]> = [
    ['Conversations', t.conversations],
    ['Messages', t.messages],
    ['Calcs', t.calcs],
    ['Engaged (4+ msgs)', t.engaged_4plus],
    ['Reached calc', t.reached_calc],
    ['Recalced (2+)', t.recalced],
    ['Avg msgs / conv', t.avg_msgs ?? '—'],
  ]
  // US CTA funnel. Since the 2026-07-22 WhatsApp-primary switch the US card
  // leads with the WhatsApp deep link and demotes SMS to a secondary button,
  // so the US conversion is split across two channels. cta_shown_at is
  // channel-agnostic (stamped at result delivery) so "US CTA shown" still uses
  // sms_cta_shown; the headline rate is now the ANY-channel rate so it doesn't
  // appear to collapse as taps migrate from SMS to WhatsApp. plan_* metrics are
  // intl-only — US sessions never generate a web plan.
  const usCtaShown = t.sms_cta_shown || 0
  const smsCtaClicked = t.sms_cta_clicked || 0
  const waCtaClickedUs = t.wa_cta_clicked_us || 0
  const waCtaClickedAll = t.handoff_wa_clicked || 0
  const usCtaClickedAny = t.us_cta_clicked_any || 0
  const usCtaRate =
    usCtaShown > 0 ? `${Math.round((usCtaClickedAny / usCtaShown) * 100)}%` : '—'
  const usCtaRedeemed = t.sms_cta_redeemed || 0
  // Closed loop: coach picked the user up. handoff_redeemed_at is stamped by
  // the worker on GET /handoff/:token (the coach's server-to-server profile
  // fetch on first inbound), which is CHANNEL-AGNOSTIC — the SMS coach and the
  // WhatsApp arm both hit it — so this already counts WhatsApp redemptions.
  // Rate is redeemed / any-channel click: the tap -> coach drop.
  const usCtaRedeemRate =
    usCtaClickedAny > 0 ? `${Math.round((usCtaRedeemed / usCtaClickedAny) * 100)}%` : '—'
  const planCtaShown = t.plan_cta_shown || 0
  const planCtaConverted = t.plan_cta_converted || 0
  const planCtaRate =
    planCtaShown > 0 ? `${Math.round((planCtaConverted / planCtaShown) * 100)}%` : '—'
  const sessions = t.conversations || 0
  const researchCards: Array<[string, string | number]> = [
    ['Sessions', sessions],
    ['US CTA shown', usCtaShown],
    ['WhatsApp clicked (US)', waCtaClickedUs],
    ['SMS clicked (US)', smsCtaClicked],
    ['US CTA clicked (any)', usCtaClickedAny],
    ['US CTA rate (any)', usCtaRate],
    ['Coach redeemed (US)', usCtaRedeemed],
    ['Redeem rate (US)', usCtaRedeemRate],
    ['WhatsApp clicked (all)', waCtaClickedAll],
    ['Plan generated (intl)', planCtaShown],
    ['CTA converted (intl)', planCtaConverted],
    ['CTA rate (intl)', planCtaRate],
  ]

  const labels =
    tab === 'daily'
      ? stats.daily.map((d) => d.date.slice(5))
      : stats.weekly.map((d) => d.week_start.slice(5))
  const points = tab === 'daily' ? stats.daily : stats.weekly
  const series: Series[] = [
    { key: 'sessions', label: 'Sessions', color: COLORS.sessions, values: points.map((p) => p.sessions) },
    { key: 'messages', label: 'Messages', color: COLORS.messages, values: points.map((p) => p.messages) },
    { key: 'calcs',    label: 'Calcs',    color: COLORS.calcs,    values: points.map((p) => p.calcs) },
  ]

  return (
    <>
      <DailyUsersPanel stats={stats} />

      <section className="cards">
        {cards.map(([label, value]) => (
          <div className="card" key={label}>
            <div className="card-label">{label}</div>
            <div className="card-value tabular">{value}</div>
          </div>
        ))}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>Product research</h2>
        </div>
        <div className="cards">
          {researchCards.map(([label, value]) => (
            <div className="card" key={label}>
              <div className="card-label">{label}</div>
              <div className="card-value tabular">{value}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>Engagement trend</h2>
          <div className="tabs">
            <button
              className={tab === 'daily' ? 'tab on' : 'tab'}
              onClick={() => setTab('daily')}
            >
              Daily · 30d
            </button>
            <button
              className={tab === 'weekly' ? 'tab on' : 'tab'}
              onClick={() => setTab('weekly')}
            >
              Weekly · 12w
            </button>
          </div>
        </div>
        <LineChart labels={labels} series={series} />
      </section>

      <section className="grid-2">
        <div className="panel">
          <div className="panel-head"><h2>Countries</h2></div>
          <CountriesTable rows={stats.countries} />
        </div>

        <div className="panel">
          <div className="panel-head"><h2>Demographics</h2></div>
          <DemoTable title="Sex" rows={stats.demographics.sex.map((r) => [String(r.sex), Number(r.n)])} />
          <DemoTable title="Age" rows={stats.demographics.age_buckets.map((r) => [String(r.bucket), Number(r.n)])} />
          <DemoTable
            title="Occupation"
            rows={stats.demographics.occupation.map((r) => [String(r.occupation), Number(r.n)])}
          />
        </div>
      </section>
    </>
  )
}

// The dashboard's hero: daily users, stacked by where they came from.
//
// Top of the page and bar-shaped on purpose — the founding question this
// answers is "how many people showed up today and which channel sent them",
// which is a magnitude-per-day question. The source split lives INSIDE the
// bars rather than in a separate table below, so a spend change and its
// traffic response are read in one glance instead of two scrolls.
//
// Degrades cleanly: a worker deployed before daily_sources existed sends no
// channel data, and the panel falls back to plain session bars.
function DailyUsersPanel({ stats }: { stats: StatsResponse }) {
  const [window, setWindow] = useState<7 | 14 | 30>(30)
  const daily = stats.daily.slice(-window)
  const sourceRows = stats.daily_sources?.slice(-window) ?? []
  const bySource = sourceRows.length === daily.length && sourceRows.length > 0

  const labels = daily.map((d) => d.date.slice(5))
  // Only stack channels that actually appear in the window — an empty legend
  // entry for every possible channel makes a low-traffic week unreadable.
  const activeChannels = SOURCE_ORDER.filter((key) =>
    sourceRows.some((r) => (r.channels[key] ?? 0) > 0)
  )
  // Any channel the server added that this build doesn't know about is folded
  // into Other rather than silently dropped from the totals.
  const knownKeys = new Set<string>(SOURCE_ORDER)
  const unknownPerDay = sourceRows.map((r) =>
    Object.entries(r.channels).reduce((sum, [k, v]) => (knownKeys.has(k) ? sum : sum + v), 0)
  )
  const hasUnknown = unknownPerDay.some((v) => v > 0)

  const series: Series[] = bySource
    ? activeChannels.map((key) => ({
        key,
        label: SOURCE_META[key].label,
        color: SOURCE_META[key].color,
        values: sourceRows.map(
          (r, i) => (r.channels[key] ?? 0) + (key === 'other' && hasUnknown ? unknownPerDay[i] : 0)
        ),
      }))
    : [
        {
          key: 'sessions',
          label: 'Sessions',
          color: COLORS.sessions,
          values: daily.map((d) => d.sessions),
        },
      ]

  const total = daily.reduce((s, d) => s + d.sessions, 0)
  const avg = daily.length ? Math.round((total / daily.length) * 10) / 10 : 0
  const today = daily.length ? daily[daily.length - 1].sessions : 0
  // Trailing-7 vs the 7 before it: the smallest comparison that survives
  // weekday seasonality, and it needs 14 days of window to mean anything.
  const last7 = stats.daily.slice(-7).reduce((s, d) => s + d.sessions, 0)
  const prev7 = stats.daily.slice(-14, -7).reduce((s, d) => s + d.sessions, 0)
  const wow = prev7 > 0 ? Math.round(((last7 - prev7) / prev7) * 100) : null

  return (
    <section className="panel panel-hero">
      <div className="panel-head">
        <h2>Daily users by source</h2>
        <div className="tabs">
          {([7, 14, 30] as const).map((w) => (
            <button
              key={w}
              className={window === w ? 'tab on' : 'tab'}
              onClick={() => setWindow(w)}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>
      <div className="hero-stats">
        <div className="hero-stat">
          <div className="card-label">Today (UTC)</div>
          <div className="card-value tabular">{today}</div>
        </div>
        <div className="hero-stat">
          <div className="card-label">Avg / day · {window}d</div>
          <div className="card-value tabular">{avg}</div>
        </div>
        <div className="hero-stat">
          <div className="card-label">Total · {window}d</div>
          <div className="card-value tabular">{total}</div>
        </div>
        <div className="hero-stat">
          <div className="card-label">Last 7d vs prior 7d</div>
          <div className={`card-value tabular ${wow != null && wow < 0 ? 'neg' : 'pos'}`}>
            {wow == null ? '—' : `${wow > 0 ? '+' : ''}${wow}%`}
          </div>
        </div>
      </div>
      <BarChart labels={labels} series={series} />
      {!bySource && (
        <p className="muted small">
          Source split unavailable — the worker did not return per-day attribution.
        </p>
      )}
    </section>
  )
}

// Pareto-truncated country table: show the leading countries that together
// account for ~80% of sessions, then collapse everything else into one
// "Other" row. Keeps the table readable when the long tail is dozens of
// single-session countries.
function CountriesTable({
  rows,
}: {
  rows: Array<{ country: string; sessions: number; calcs: number }>
}) {
  if (rows.length === 0) {
    return (
      <table className="data">
        <thead>
          <tr><th>Country</th><th className="num">Sessions</th><th className="num">Calcs</th></tr>
        </thead>
        <tbody>
          <tr><td colSpan={3} className="muted">No data</td></tr>
        </tbody>
      </table>
    )
  }
  const totalSessions = rows.reduce((s, r) => s + (Number(r.sessions) || 0), 0)
  const sorted = [...rows].sort((a, b) => Number(b.sessions) - Number(a.sessions))
  const head: typeof sorted = []
  let cum = 0
  for (const r of sorted) {
    head.push(r)
    cum += Number(r.sessions) || 0
    if (totalSessions > 0 && cum / totalSessions >= 0.8) break
  }
  const tail = sorted.slice(head.length)
  const tailSessions = tail.reduce((s, r) => s + (Number(r.sessions) || 0), 0)
  const tailCalcs = tail.reduce((s, r) => s + (Number(r.calcs) || 0), 0)
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Country</th>
          <th className="num">Sessions</th>
          <th className="num">Calcs</th>
        </tr>
      </thead>
      <tbody>
        {head.map((row) => (
          <tr key={row.country}>
            <td>{row.country}</td>
            <td className="num tabular">{row.sessions}</td>
            <td className="num tabular">{row.calcs}</td>
          </tr>
        ))}
        {tail.length > 0 && (
          <tr className="muted">
            <td>Other ({tail.length} {tail.length === 1 ? 'country' : 'countries'})</td>
            <td className="num tabular">{tailSessions}</td>
            <td className="num tabular">{tailCalcs}</td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

// Abuse block list. Every row carries the message that triggered it, because
// the operator question is never "is this IP blocked" but "should it be" —
// corporate NAT and carrier CGNAT put many people behind one address, so a
// block has to be reviewable and one click to lift.
function BlockedIpsPanel({ token }: { token: string }) {
  const [rows, setRows] = useState<BlockedIpRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRows(await fetchBlockedIps(token))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed')
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const toggle = useCallback(
    async (ip: string, blocked: boolean) => {
      setBusy(ip)
      try {
        await setIpBlocked(token, ip, blocked)
        await load()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'update failed')
      } finally {
        setBusy(null)
      }
    },
    [token, load]
  )

  const active = (rows ?? []).filter((r) => r.unblocked_at == null)
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>
          Blocked IPs {rows ? `(${active.length} active / ${rows.length} total)` : ''}
        </h2>
        <button className="ghost" onClick={() => void load()}>
          Refresh
        </button>
      </div>
      <p className="muted small">
        Set automatically when a connection attempts system-prompt extraction. A blocked
        connection gets a fixed in-chat line and never reaches the model.
      </p>
      {error && <div className="admin-error">{error}</div>}
      <table className="data compact">
        <thead>
          <tr>
            <th>IP</th>
            <th>Status</th>
            <th className="num">Hits</th>
            <th>First</th>
            <th>Last</th>
            <th>Reason</th>
            <th>Triggering message</th>
            <th>Session</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows === null && (
            <tr>
              <td colSpan={9} className="muted">
                Loading…
              </td>
            </tr>
          )}
          {rows?.length === 0 && (
            <tr>
              <td colSpan={9} className="muted">
                No blocks yet.
              </td>
            </tr>
          )}
          {rows?.map((r) => (
            <tr key={r.ip}>
              <td className="tabular">{r.ip}</td>
              <td>
                <span className={r.unblocked_at == null ? 'src-pill src-other' : 'muted'}>
                  {r.unblocked_at == null ? 'blocked' : 'lifted'}
                </span>
              </td>
              <td className="num tabular">{r.hits}</td>
              <td className="tabular">{new Date(r.blocked_at).toLocaleString()}</td>
              <td className="tabular">{new Date(r.last_seen_at).toLocaleString()}</td>
              <td>{r.reason}</td>
              <td className="muted" title={r.sample ?? ''}>
                {r.sample ? `${r.sample.slice(0, 90)}…` : '—'}
              </td>
              <td className="tabular">
                {r.conversation_id ? (
                  <a className="session-link" href={`#/sessions/${r.conversation_id}`}>
                    {r.conversation_id.slice(0, 8)}…
                  </a>
                ) : (
                  '—'
                )}
              </td>
              <td>
                <button
                  className="ghost"
                  disabled={busy === r.ip}
                  onClick={() => void toggle(r.ip, r.unblocked_at != null)}
                >
                  {busy === r.ip
                    ? '…'
                    : r.unblocked_at == null
                      ? 'Unblock'
                      : 'Re-block'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

function EmailOutboxPanel({ token }: { token: string }) {
  const [log, setLog] = useState<EmailLogResponse | null>(null)
  // For real sends the server now runs work in ctx.waitUntil, so the HTTP
  // response returns in <1s regardless of size. A default of 10 is a
  // comfortable click-once-then-walk-away size; user can crank to 200.
  const [batchSize, setBatchSize] = useState(10)
  // Log fetch size — server caps at 200.
  const [logLimit, setLogLimit] = useState(50)
  const [logTab, setLogTab] = useState<'sent' | 'received'>('sent')
  const [testTo, setTestTo] = useState('')
  const [busy, setBusy] = useState<'dry' | 'test' | 'real' | null>(null)
  const [lastResult, setLastResult] = useState<SendBatchResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reloadLog = useCallback(async () => {
    try {
      const r = await fetchEmailLog(token, logLimit)
      setLog(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'log load failed')
    }
  }, [token, logLimit])

  useEffect(() => {
    void reloadLog()
  }, [reloadLog])

  const run = useCallback(
    async (mode: 'dry' | 'test' | 'real') => {
      if (mode === 'test' && !testTo) {
        setError('Enter a test_to email first')
        return
      }
      setBusy(mode)
      setError(null)
      setLastResult(null)
      try {
        const args: { batch_size: number; dry_run?: boolean; test_to?: string } = {
          batch_size: batchSize,
        }
        if (mode === 'dry') args.dry_run = true
        if (mode === 'test') args.test_to = testTo
        const r = await sendEmailBatch(token, args)
        setLastResult(r)
        await reloadLog()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'send failed')
      } finally {
        setBusy(null)
      }
    },
    [batchSize, testTo, token, reloadLog]
  )

  const sentRows = log?.sent ?? []
  const receivedRows = log?.received ?? []

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Email outbox</h2>
        <button className="ghost" onClick={() => void reloadLog()}>
          Refresh
        </button>
      </div>

      <div className="email-controls">
        <label className="email-ctrl">
          <span>Batch size</span>
          <input
            type="number"
            min={1}
            max={200}
            value={batchSize}
            onChange={(e) => setBatchSize(Math.max(1, Math.min(200, Number(e.target.value) || 10)))}
          />
        </label>
        <label className="email-ctrl">
          <span>Log rows</span>
          <input
            type="number"
            min={1}
            max={200}
            value={logLimit}
            onChange={(e) => setLogLimit(Math.max(1, Math.min(200, Number(e.target.value) || 50)))}
          />
        </label>
        <label className="email-ctrl">
          <span>Test to (overrides recipients)</span>
          <input
            type="email"
            placeholder="you@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
        </label>
        <div className="email-buttons">
          <button onClick={() => void run('dry')} disabled={busy !== null}>
            {busy === 'dry' ? 'Running…' : 'Dry run'}
          </button>
          <button onClick={() => void run('test')} disabled={busy !== null || !testTo}>
            {busy === 'test' ? 'Sending…' : 'Send to test_to'}
          </button>
          <button
            className="primary"
            onClick={() => {
              if (
                window.confirm(
                  `Send up to ${batchSize} REAL initial-plan emails to captured recipients? This cannot be undone.`
                )
              ) {
                void run('real')
              }
            }}
            disabled={busy !== null}
          >
            {busy === 'real' ? 'Sending…' : 'Send for real'}
          </button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {lastResult && (
        <div className="email-result">
          <div className="muted" style={{ marginBottom: 8 }}>
            requested={lastResult.requested} · candidates={lastResult.candidates} ·{' '}
            {lastResult.mode === 'async' ? (
              <>
                <strong>{lastResult.claimed} claimed in background</strong>
                {typeof lastResult.remaining_after === 'number' && lastResult.remaining_after > 0 ? (
                  <>
                    {' '}
                    · <strong>{lastResult.remaining_after} more waiting</strong> — click Send for
                    real again in ~60s to pick up the next slice
                  </>
                ) : (
                  ' — refresh the Sent tab in ~30s to see them'
                )}
              </>
            ) : (
              <>
                dry_run={String(lastResult.dry_run)} · test_to={lastResult.test_to ?? '—'}
              </>
            )}
          </div>
          <table className="data compact">
            <thead>
              <tr>
                <th>Conv</th>
                <th>Status</th>
                <th>To</th>
                <th>Subject</th>
                <th>Plan</th>
                <th>Provider ID / Error</th>
              </tr>
            </thead>
            <tbody>
              {lastResult.results.map((r, i) => (
                <tr key={i}>
                  <td className="tabular">
                    <a className="session-link" href={`#/sessions/${r.conversation_id}`}>
                      {r.conversation_id.slice(0, 8)}…
                    </a>
                  </td>
                  <td>{r.status}</td>
                  <td>{r.to ?? '—'}</td>
                  <td>{r.subject ?? '—'}</td>
                  <td>{r.plan_was_freshly_generated ? 'fresh' : 'cached'}</td>
                  <td className="tabular">{r.provider_msg_id ?? r.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="email-log-tabs">
        <button
          type="button"
          className={logTab === 'sent' ? 'email-log-tab on' : 'email-log-tab'}
          onClick={() => setLogTab('sent')}
        >
          Sent ({sentRows.length})
        </button>
        <button
          type="button"
          className={logTab === 'received' ? 'email-log-tab on' : 'email-log-tab'}
          onClick={() => setLogTab('received')}
        >
          Replies ({receivedRows.length})
        </button>
      </div>

      {logTab === 'sent' ? (
        <table className="data compact">
          <thead>
            <tr>
              <th>When</th>
              <th>Conv</th>
              <th>Kind</th>
              <th>To</th>
              <th>Subject</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {sentRows.length === 0 && (
              <tr><td colSpan={6} className="muted">No sent emails yet.</td></tr>
            )}
            {sentRows.map((r) => (
              <tr key={r.id}>
                <td className="tabular">{new Date(r.sent_at).toLocaleString()}</td>
                <td className="tabular">
                  <a className="session-link" href={`#/sessions/${r.conversation_id}`}>
                    {r.conversation_id.slice(0, 8)}…
                  </a>
                </td>
                <td>{r.kind}</td>
                <td>{r.to_addr}</td>
                <td>{r.subject}</td>
                <td>{r.error ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="data compact">
          <thead>
            <tr>
              <th>When</th>
              <th>Conv</th>
              <th>From</th>
              <th>Subject</th>
              <th>Preview</th>
            </tr>
          </thead>
          <tbody>
            {receivedRows.length === 0 && (
              <tr><td colSpan={5} className="muted">No replies yet.</td></tr>
            )}
            {receivedRows.map((r) => (
              <tr key={r.id}>
                <td className="tabular">{new Date(r.received_at).toLocaleString()}</td>
                <td className="tabular">
                  <a className="session-link" href={`#/sessions/${r.conversation_id}`}>
                    {r.conversation_id.slice(0, 8)}…
                  </a>
                </td>
                <td>{r.from_addr}</td>
                <td>{r.subject ?? '—'}</td>
                <td className="muted">{r.body_preview}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function DemoTable({ title, rows }: { title: string; rows: Array<[string, number]> }) {
  return (
    <div className="demo-block">
      <div className="demo-title">{title}</div>
      <table className="data compact">
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={2} className="muted">No data</td></tr>
          )}
          {rows.map(([k, n]) => (
            <tr key={k}>
              <td>{k}</td>
              <td className="num tabular">{n}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
